import { cn } from 'noddi-ui-common';
import { webButtonStyling } from '../../../../helpers/webButtonStyling';
import { TableQuickFilter } from '../../../../types/table';
import { NoddiBadge } from '../../../Other/NoddiBadge';

interface Props<TValue extends string> {
  quickFilter: TableQuickFilter<TValue>;
}

/**
 * Segmented tray for a list's quick filter — one 36px control holding two or three 30px options,
 * right-aligned next to View. Single-select and always one deep, so it reads as the list's scope
 * rather than as another filter chip.
 */
export function QuickFilterTray<TValue extends string>({ quickFilter }: Props<TValue>) {
  const { label, options, selectedValue, onChange } = quickFilter;

  return (
    <>
      <div
        role='group'
        aria-label={label}
        // Shrinks and scrolls sideways where four options with counts are wider than a phone; `shrink-0` here
        // set the toolbar's minimum width, so the whole page scrolled sideways instead.
        className='flex h-9 min-w-0 items-center gap-0.5 overflow-x-auto rounded-[9px] bg-muted p-[3px]'
      >
        {options.map((option) => {
          const isSelected = option.value === selectedValue;

          return (
            <button
              key={option.value}
              type='button'
              aria-pressed={isSelected}
              onClick={() => onChange(option.value)}
              className={cn(
                webButtonStyling,
                'flex h-[30px] items-center gap-1.5 rounded-[7px] px-3 text-[13px] whitespace-nowrap text-darkPurple',
                isSelected ? 'bg-white font-semibold shadow-xs' : 'font-medium hover:bg-white/70'
              )}
            >
              {option.label}
              {option.count !== undefined && (
                <NoddiBadge
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'px-1.5 py-0.5 text-[11px]',
                    isSelected ? 'font-semibold' : 'border-transparent bg-white text-greyMuted'
                  )}
                >
                  {option.count}
                </NoddiBadge>
              )}
            </button>
          );
        })}
      </div>
      {/* Separates the scope from the controls that act on it, so the tray does not read as a third button. */}
      <span aria-hidden className='h-6 w-px shrink-0 bg-border' />
    </>
  );
}
